// Chargement au défilement (« scroll infini »).
//
// Le fil ne charge plus tout : la première page arrive en direct par
// l'abonnement, et les suivantes sont demandées quand on approche du bas.
// Le coût d'ouverture de l'app ne grandit donc plus avec le nombre de
// publications — c'est la limite n°1 relevée dans ARCHITECTURE.md.

import { esc } from "./ui.js";

/**
 * @param {object} o
 * @param {HTMLElement} o.conteneur  élément qui reçoit la liste
 * @param {function} o.chargerPage   ({avant}) => Promise<tableau>
 * @param {function} o.afficher      (tout) => void, appelée à chaque évolution
 */
export function creerDefilement({ conteneur, chargerPage, afficher }) {
  let recentes = [];   // première page, tenue à jour en direct
  let anciennes = [];  // pages suivantes, chargées au défilement
  let fini = false;
  let enCours = false;

  // La sentinelle reste TOUJOURS dans la mise en page : un élément masqué
  // (display:none) n'est jamais vu par l'observateur, et rien ne se chargerait.
  const sentinelle = document.createElement("div");
  sentinelle.className = "sentinelle";
  const rouage = document.createElement("div");
  rouage.className = "spinner";
  rouage.style.visibility = "hidden";
  sentinelle.appendChild(rouage);
  conteneur.after(sentinelle);

  const tout = () => {
    // la première page peut recouper les suivantes : on dédoublonne par identifiant
    const vus = new Set(recentes.map((p) => p.id));
    return [...recentes, ...anciennes.filter((p) => !vus.has(p.id))];
  };

  async function charger() {
    if (fini || enCours) return;
    const liste = tout();
    if (!liste.length) return;
    enCours = true;
    rouage.style.visibility = "visible";
    try {
      const suite = await chargerPage({ avant: liste[liste.length - 1].createdAt });
      const connus = new Set(liste.map((p) => p.id));
      const nouvelles = suite.filter((p) => !connus.has(p.id));
      if (!nouvelles.length) {
        fini = true;
        sentinelle.innerHTML = `<p class="petit texte-doux">Vous avez tout vu 🕊️</p>`;
      } else {
        anciennes = [...anciennes, ...nouvelles];
        afficher(tout());
      }
    } catch (e) {
      /* ⚠️ NE JAMAIS AFFICHER LE MESSAGE BRUT.
         Firestore renvoie des textes techniques — « The query requires an
         index. You can create it here: https://console.firebase.google.com/… » —
         et ils s'affichaient tels quels, en anglais, au milieu du fil, sous les
         yeux des membres. C'est illisible, ça fait peur, et ça expose l'adresse
         d'administration du projet.
         Le membre lit une phrase en français ; le détail part dans la console
         pour qu'on puisse le diagnostiquer. */
      console.warn("Défilement : chargement de la page suivante impossible —", e?.message || e);
      sentinelle.innerHTML = `<p class="petit texte-doux">Impossible de charger la suite. Réessayez dans un instant.</p>`;
    } finally {
      enCours = false;
      if (!fini) rouage.style.visibility = "hidden";
    }
  }

  // on déclenche un peu avant le bas, pour que la suite soit déjà là
  const observateur = new IntersectionObserver((entrees) => entrees[0].isIntersecting && charger(), {
    rootMargin: "600px",
  });
  observateur.observe(sentinelle);

  return {
    /** Nouvelle première page reçue de l'abonnement temps réel. */
    majRecentes(liste) {
      recentes = liste;
      afficher(tout());
    },
    /** Redemande un affichage sans recharger : le classement a changé d'avis. */
    rafraichir() {
      afficher(tout());
    },
    detruire() {
      observateur.disconnect();
      sentinelle.remove();
    },
  };
}
